import { FileDropZone } from "@similarweb/ui-components";
import { HiddenFileInput } from "@similarweb/ui-components";
import type { IServices } from "../../CustomHooks/useMarketBuilderServices";
import type { FC } from "react";
import { useState } from "react";
import {
    CSVFileDropZoneBoxContainer,
    CSVFileDropZoneContainer,
    CSVFileDropZoneImage,
    CSVFileDropZoneText,
    ErrorMessage,
    ProcessingSpinner,
} from "./ImportDomainsModal.styles";
import { allTrackers } from "app/services/track/track";

interface ICSVFileDropZoneProps {
    handleFiles: Function;
    acceptFile?: string;
    error?: string;
    services: IServices;
}

interface ICSVFileDropZoneBoxProps {
    handleFiles: Function;
    isOver: boolean;
    isProcessing: boolean;
    isError: boolean;
    acceptFile?: string;
    services: IServices;
}

enum EUploadType {
    UPLOAD,
    DROP,
}

export const CSVFileDropZone: FC<ICSVFileDropZoneProps> = ({ handleFiles, acceptFile, error, services }) => {
    const [isProcessing, setIsProcessing] = useState(false);

    const processFiles = (uploadType) => {
        return async (...args) => {
            setIsProcessing(true);
            try {
                await handleFiles(...args);
                if (uploadType === EUploadType.DROP) {
                    allTrackers.trackEvent("drop", "drop", "By websites/dropFile/Success");
                } else {
                    allTrackers.trackEvent("upload", "upload", "By websites/uploadFile/Success");
                }
            } catch {
                if (uploadType === EUploadType.DROP) {
                    allTrackers.trackEvent("drop", "drop", "By websites/dropFile/Fail");
                } else {
                    allTrackers.trackEvent("upload", "upload", "By websites/uploadFile/Fail");
                }
            } finally {
                setIsProcessing(false);
            }
        };
    };

    return (
        <CSVFileDropZoneContainer>
            <FileDropZone
                onDragDrop={processFiles(EUploadType.DROP)}
                isDisabled={isProcessing}
            >
                {({ isOver }) => (
                    <CSVFileDropZoneBox
                        handleFiles={processFiles(EUploadType.UPLOAD)}
                        isOver={isOver}
                        isProcessing={isProcessing}
                        isError={Boolean(error)}
                        acceptFile={acceptFile}
                        services={services}
                    />
                )}
            </FileDropZone>
            {error && <ErrorMessage>{error}</ErrorMessage>}
        </CSVFileDropZoneContainer>
    );
};

const CSVFileDropZoneBox: FC<ICSVFileDropZoneBoxProps> = ({
    handleFiles,
    isOver,
    isProcessing,
    isError,
    acceptFile,
    services,
}) => {
    const renderDropZoneText = () => {
        if (isProcessing) {
            return (
                <CSVFileDropZoneText>
                    <ProcessingSpinner />
                    {services.translate("marketbuilder.importModal.uploading")}
                </CSVFileDropZoneText>
            );
        }
        if (isOver) {
            return (
                <CSVFileDropZoneText>
                    <a>{services.translate("marketbuilder.importModal.dropHere")}</a>
                </CSVFileDropZoneText>
            );
        }
        return (
            <CSVFileDropZoneText>
                <HiddenFileInput
                    onFileChange={handleFiles}
                    accept={acceptFile}
                >
                    <a>{services.translate("marketbuilder.importModal.upload.part1")}</a>
                </HiddenFileInput>
                &nbsp;{services.translate("marketbuilder.importModal.upload.part2")}
            </CSVFileDropZoneText>
        );
    };

    return (
        <CSVFileDropZoneBoxContainer
            isOver={isOver}
            isProcessing={isProcessing}
            isError={isError}
        >
            {!isProcessing && <CSVFileDropZoneImage />}
            {renderDropZoneText()}
        </CSVFileDropZoneBoxContainer>
    );
};
